import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { RouteDefinitions } from 'routes';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Switch } from '@televet/kibble-ui/build/components/Switch';
import { HStack, Flex, Center } from '@televet/kibble-ui/build/chakra';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { toggleWallpaperMode } from 'state/currentClinic/currentClinicSlice';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useAuth } from 'shared/providers/AuthProvider';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import wallpaperUrl from 'assets/images/wallpaper.jpeg';
import { useTopToolbarContext } from './context/TopToolbarContext';
import ProcessTracker from '../ProcessTracker';

enum PageName {
  appointments = 'appointments',
  board = 'board',
  conversations = 'conversations',
  team = 'team',
  loyaltyProgram = 'loyalty-program',
  emails = 'emails',
  emailTemplates = 'email-templates',
  survey = 'survey',
  reporting = 'reporting',
  automations = 'automations',
  templates = 'templates',
  forms = 'forms',
  settings = 'settings',
  channelAttachments = 'channelattachments',
  successCenter = 'success-center',
  ottoEmailCampaigns = 'email-campaigns',
  globalAutomations = 'global-automations',
  tasks = 'tasks',
}

const TopToolbar = (): JSX.Element => {
  const { isMobile } = useResolutionProvider();
  const location = useLocation();
  const pageName = location.pathname.split('/')[1];
  const { currentClinicId } = useClinicUser();

  const wallpaperMode = useAppSelector((state) => state.currentClinic.wallpaperMode);
  const dispatch = useAppDispatch();
  const { topToolbarAdditionalContent, overridePageName } = useTopToolbarContext();

  const createPageName = useMemo((): string | undefined => {
    if (overridePageName) {
      return overridePageName;
    }
    switch (pageName) {
      case PageName.appointments:
        return 'Appointments';
      case PageName.board:
        return 'Board';
      case PageName.conversations:
        return 'Conversations';
      case PageName.team:
        return 'Team Chat';
      case PageName.loyaltyProgram:
        return 'Loyalty Program';
      case PageName.emails:
        return 'Email Campaigns';
      case PageName.survey:
        return 'Survey';
      case PageName.reporting:
        return 'Reporting';
      case PageName.automations:
        return 'Automations';
      case PageName.templates:
        return 'Message Templates';
      case PageName.forms:
        return 'Forms';
      case PageName.settings:
      case PageName.emailTemplates:
        return 'Settings';
      case PageName.channelAttachments:
        return 'Conversation Attachment';
      case PageName.successCenter:
        return 'Success Hub';
      case PageName.ottoEmailCampaigns:
        return 'Email Campaigns';
      case PageName.globalAutomations:
        return 'Global Automations';
      case PageName.tasks:
        return 'Tasks';
    }
  }, [pageName, overridePageName]);

  const isAprilFoolsDay = useMemo(() => {
    const date = new Date();
    return date.getMonth() === 3 && date.getDate() === 1;
  }, []);

  const { isImpersonating } = useAuth();
  const { isOttoTeamMember } = useClinicUser();

  useEffect(() => {
    if (!isAprilFoolsDay && wallpaperMode) {
      dispatch(toggleWallpaperMode());
    }
  }, [isAprilFoolsDay, wallpaperMode, dispatch]);

  if (RouteDefinitions.Board.includes(location.pathname)) {
    return <></>;
  }

  return (
    <HStack
      p={5}
      h={14}
      backgroundColor="background.default"
      display={!isMobile ? 'flex' : 'none'}
      borderBottom="1px"
      borderBottomColor="border.default"
      justify="space-between"
      w="100%"
      {...(wallpaperMode
        ? {
            backgroundImage: `url(${wallpaperUrl})`,
            backgroundRepeat: 'repeat',
            backgroundSize: '14%',
          }
        : {})}
    >
      <HStack gap={2} w="100%">
        <Heading
          size="md"
          {...(wallpaperMode
            ? {
                bg: 'white',
                py: 2,
                px: 3,
                border: '1px solid',
                borderColor: 'border.default',
                borderRadius: 'xl',
              }
            : {})}
        >
          {createPageName || 'No page name'}
        </Heading>
        {isAprilFoolsDay && (isOttoTeamMember || isImpersonating) && (
          <Flex align="center" p={2} bg="white" border="1px solid" borderColor="border.default" borderRadius="xl">
            <Switch
              label="Wallpaper Mode"
              formControlStyle={{ display: 'flex' }}
              labelStyle={{ flex: '0 0 auto', margin: '0 0 0 8px', cursor: 'pointer' }}
              labelPosition="end"
              isChecked={wallpaperMode}
              onChange={(): void => {
                dispatch(toggleWallpaperMode());
              }}
            />
            <Tooltip label="You voted and we delivered! (This is only visible to Otto team members.)">
              <Center ml={2}>
                <Icon name="infoCircle" size="sm" />
              </Center>
            </Tooltip>
          </Flex>
        )}
        {topToolbarAdditionalContent}
      </HStack>
      {currentClinicId && <ProcessTracker />}
    </HStack>
  );
};

export default TopToolbar;
